import type { PairSimProfile, SimProfile } from "@opus-dt/shared-types";
import type { SandboxMachine } from "@/types/sim-sandbox.types";

// Bases de EXIBIÇÃO vindas do preview do servidor, extraído do store
// (max-lines). São display-only: entram no rascunho sem marcar dirty e ficam fora do snapshot,
// porque dependem da janela de calibração e do produto ativo — re-resolvem a cada preview.

export interface MeasuredBaseUpdate {
  baseProfile: SimProfile;
  counterConfigured: boolean;
  producedInWindow: number;
  /** Perfil do par com o produto ativo, resolvido pelo servidor. */
  pairProfile?: PairSimProfile | null;
}

// Igualdade dos campos que importam p/ exibição (5 params + proveniência) — guarda do merge.
// Compara também provenanceByParam — mudança só de ORIGEM (ex.: feed vira medido
// com o mesmo valor) precisa propagar, senão o badge do sheet fica obsoleto.
const PROV_PARAMS = ["feedRate", "quality", "mtbf", "mttr"] as const;

function sameProfile(a: SimProfile | undefined, b: SimProfile): boolean {
  return (
    !!a &&
    a.provenance === b.provenance &&
    a.feedRateUnH === b.feedRateUnH &&
    a.cycleTimeS === b.cycleTimeS &&
    a.mtbfMin === b.mtbfMin &&
    a.mttrMin === b.mttrMin &&
    a.qualityTargetPct === b.qualityTargetPct &&
    PROV_PARAMS.every((k) => {
      const pa = a.provenanceByParam?.[k];
      const pb = b.provenanceByParam?.[k];
      return pa?.provenance === pb?.provenance && pa?.sampleCount === pb?.sampleCount;
    })
  );
}

function samePair(a: PairSimProfile | null | undefined, b: PairSimProfile | null | undefined) {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

/**
 * Aplica as bases do preview no mapa de máquinas. Devolve `null` quando nada mudou, para o store
 * preservar a referência anterior (evita re-render em cada preview idêntico).
 */
export function mergeMeasuredBases(
  machines: Record<string, SandboxMachine>,
  bases: Record<string, MeasuredBaseUpdate>
): Record<string, SandboxMachine> | null {
  let changed = false;
  const next: Record<string, SandboxMachine> = {};
  for (const [key, m] of Object.entries(machines)) {
    const update = bases[key];
    // Contador/volume acompanham a base (decidem o aviso no sheet).
    const sameCounter =
      m.productionCounter?.configured === update?.counterConfigured &&
      m.productionCounter?.producedInWindow === update?.producedInWindow;
    // O par também precisa propagar: editar o perfil do produto deve mudar o preview
    // local sem F5 (senão o preview mente sobre o parâmetro que a rodada vai usar).
    if (
      update &&
      (!sameProfile(m.measuredBase, update.baseProfile) ||
        !sameCounter ||
        !samePair(m.pairProfile, update.pairProfile))
    ) {
      next[key] = {
        ...m,
        measuredBase: update.baseProfile,
        pairProfile: update.pairProfile ?? null,
        productionCounter: {
          configured: update.counterConfigured,
          producedInWindow: update.producedInWindow,
        },
      };
      changed = true;
    } else {
      next[key] = m;
    }
  }
  return changed ? next : null;
}
